import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { cookieJar, LoanOfferT } from '@consumer-frontend/api';
import { PageValuesT } from '@consumer-frontend/shared-ui';

import {
  ContractFlowInputsT,
  ContractStepKeyT,
  getContractSteps,
  isSupportedContractLoanType,
  STEP_KEYS,
} from '../config';
import {
  AlertBannerPropsT,
  ContractT,
  DrawLimitsT,
  FIELD_ANCHORS,
  initialAlertBannerProps,
  initialDrawLimits,
  initialModalConfig,
  ModalConfigT,
  PaymentMethodT,
  SectionT,
} from './constants';
import { AUTHORIZATIONS_POSTSCRIPT_SECTION_NAME, buildContractSections } from './contractBuilder';
import { isCnuDrawInputValid, isPaymentMethodValid, isRepaymentMethodMismatched } from './validators';

// The flowId only lives on the URL as `?flowId=...` on the very first load — internal
// navigation (e.g. ContractIndexRedirect's redirect to the first step) doesn't carry query
// params forward. RootLayout captures it into this atom once, on mount, so the rest of the flow
// can read it without depending on the URL still having it.
//
// Backed by sessionStorage (not a plain atom) so a mid-flow page refresh doesn't lose it too —
// without this, any reload past the first step finds an empty flowId and RootLayout sends the
// customer to account home. `getOnInit: true` reads the persisted value synchronously on the
// very first read, rather than jotai's SSR-safe default of only reading it after mount (this is
// a client-only SPA, so there is no hydration mismatch to avoid).
//
// Because this now survives a reload, it must also be explicitly cleared on every way out of
// this app (see leaveContractApp in processOrchestratorResponse.ts) — otherwise a customer who
// leaves and comes back to this origin in the same tab (e.g. the back button, or a page restored
// from the browser's back-forward cache) would resume a flow they already left.
export const flowIdAtom = atomWithStorage<string>(
  'flowId',
  '',
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

export const isSmartBiddingLeadAtom = atomWithStorage<boolean>(
  'isSmartBiddingLead',
  false,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

export const accountIdAtom = atom(() => {
  const [accountId, error] = cookieJar.getAccountId();

  if (!accountId || error) {
    console.warn('Error getting account ID from cookie jar:', error, { accountId });

    return '';
  }

  return accountId;
});

export const orchestratorAccountIdAtom = atom<string>('');

export const isBlocCustomerAtom = atom(() => {
  const [tenant, error] = cookieJar.getTenant();

  if (!tenant || error) {
    console.warn('Error getting tenant from cookie jar:', error, { tenant });

    return false;
  }

  return tenant.includes('cashnetusa_bloc');
});

export const alertBannerAtom = atom<AlertBannerPropsT>(initialAlertBannerProps);

export const messageCenterPaginationAtom = atom<PageValuesT>({
  hasMore: false,
  limit: 10,
  offset: 0,
  total: 0,
});

export const messageDetailPaginationAtom = atom<PageValuesT>({
  hasMore: false,
  limit: 1,
  offset: 0,
  total: 0,
});

export const showMobileNavAtom = atom(false);

export const modalConfigAtom = atom<ModalConfigT>(initialModalConfig);

/** ***********************************
 *************************************
 *** Contract Input Atoms (store) ****
 *************************************
 ************************************ */

// Holds the current loan offer, populated from current_step/display_loan_offer's
// loan_offer.response.metadata. Deliberately a plain (non-persisted) atom: the full object
// carries fields ASVS V14.3.1/V14.3.3 don't want sitting in browser storage any longer than
// necessary (name, bank name/last4, APR, RBP report). The orchestrator team guarantees
// current_step returns a fresh loan_offer (and contract — see contractsAtom) for whatever step
// the flow is actually on, so a reload at any step just refetches rather than needing anything
// about the offer to survive it.
export const loanOfferAtom = atom<LoanOfferT | null>(null);

// Epoch ms of the first loan-offer load, set once in useLoadLoanOffer — the baseline for the
// Funds-to-sign duration computed on LocAuthPage. A bare timestamp is not PII (unlike
// loanOfferAtom's payload), so it is safe to persist: sessionStorage keeps the baseline across a
// mid-flow refresh. Cleared in clearFlowStorage so it can't leak into a later flow in the same tab.
export const loanOfferLoadedAtAtom = atomWithStorage<number | null>(
  'loanOfferLoadedAt',
  null,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

export const dirtyStepsAtom = atomWithStorage<Partial<Record<ContractStepKeyT, boolean>>>(
  'dirtySteps',
  {},
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

const markStepDirtyAtom = atom(null, (_get, set, stepKey: ContractStepKeyT) => {
  set(dirtyStepsAtom, (dirty) => ({ ...dirty, [stepKey]: true }));
});

export const reviewedSectionNamesAtom = atom<Record<string, string[]>>({});

export const contractSubmitAttemptedAtom = atom(false);

export const cnuDrawInputBaseAtom = atomWithStorage<string>(
  'cnuDrawInput',
  '',
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

export const cnuDrawInputAtom = atom(
  (get) => get(cnuDrawInputBaseAtom),
  (_get, set, value: string) => {
    set(cnuDrawInputBaseAtom, value);
    set(markStepDirtyAtom, STEP_KEYS.FUNDS);
  },
);

// Base atom now persists to sessionStorage
export const paymentMethodBaseAtom = atomWithStorage<PaymentMethodT | null>(
  'paymentMethod',
  null,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

// Wrapper keeps the side-effects on write
export const paymentMethodAtom = atom(
  (get) => get(paymentMethodBaseAtom),
  (_get, set, value: PaymentMethodT | null) => {
    set(paymentMethodBaseAtom, value);
    set(markStepDirtyAtom, STEP_KEYS.FUNDS);
    set(reviewedSectionNamesAtom, {});
    set(contractSubmitAttemptedAtom, false);
  },
);

export const contractStepAlertAtom = atom<string | null>(null);

// Contract Agreements — empty until the API populates them; stories seed their own fixtures.
//
// Deliberately a plain (non-persisted) atom, same reasoning as loanOfferAtom: the contract
// documents shouldn't sit in browser storage any longer than the step that needs them, and
// current_step is guaranteed to return them fresh for whatever step the flow is on, so a reload
// just refetches. Reset alongside the other flow atoms in leaveContractApp.
export const contractsAtom = atom<ContractT[]>([]);

export const activeContractNameAtom = atom<string | null>(null);

export const activeContractAtom = atom<ContractT | null>((get) => {
  const contracts = get(contractsAtom);
  const name = get(activeContractNameAtom);

  if (name === null) return contracts[0] ?? null;

  return contracts.find((contract) => contract.name === name) ?? null;
});

// The active contract's documents, in the order they must be reviewed.
export const activeContractSectionsAtom = atom<SectionT[]>((get) => {
  const contract = get(activeContractAtom);

  return contract
    ? buildContractSections([contract])
    : [];
});

export const activeReviewedSectionNamesAtom = atom<string[]>((get) => {
  const contract = get(activeContractAtom);

  if (!contract) return [];

  return get(reviewedSectionNamesAtom)[contract.name] ?? [];
});

export const authorizationsPostscriptHtmlAtom = atom<string | null>((get) => {
  const contract = get(activeContractAtom);

  return (
    contract?.sections.find(
      ({ name }) => name === AUTHORIZATIONS_POSTSCRIPT_SECTION_NAME,
    )?.html ?? null
  );
});

export const reviewSectionAtom = atom(null, (get, set, sectionName: string) => {
  const contract = get(activeContractAtom);

  if (!contract) return;

  set(reviewedSectionNamesAtom, (previous) => {
    const current = previous[contract.name] ?? [];

    if (current.includes(sectionName)) return previous;

    return { ...previous, [contract.name]: [...current, sectionName] };
  });
});

// Draw limits are a slice of the loan offer. The null fallback exists only so the derived
// validity atoms stay computable before the offer lands — it must never be rendered to the customer, because `initialDrawLimits.max` is a plausible-looking credit limit. Consumers
// gate on `loanOfferAtom` being non-null (see LocFundsPage).
export const drawLimitsAtom = atom<DrawLimitsT>((get) => {
  const offer = get(loanOfferAtom);

  return offer
    ? { max: offer.amount, min: offer.minimum_draw_amount }
    : initialDrawLimits;
});

// *** The User interacted with fields to trigger validation errors. These are not reset when the user goes back to a previous step. ***

export const cnuDrawTouchedAtom = atom(false);
export const paymentMethodTouchedAtom = atom(false);

// Write-only: a rejected Continue reveals every Funds error at once, rather than making the
// customer discover them one blur at a time.
export const revealFundsErrorsAtom = atom(null, (_get, set) => {
  set(cnuDrawTouchedAtom, true);
  set(paymentMethodTouchedAtom, true);
});

// Write-only: a rejected Continue on the Auth step surfaces the review-all-documents message.
export const revealAuthAgreementErrorAtom = atom(null, (_get, set) => {
  set(contractSubmitAttemptedAtom, true);
});

/** ***********************************
 *************************************
 ** Contract Input Validation Atoms **
 *************************************
 ************************************ */

export const isCnuDrawInputValidAtom = atom((get) => {
  const { min, max } = get(drawLimitsAtom);

  return isCnuDrawInputValid(get(cnuDrawInputAtom), min, max);
});

export const isPaymentMethodValidAtom = atom((get) => isPaymentMethodValid(get(paymentMethodAtom)));

// True when the customer has picked a different repayment method than the one the loan offer
// was underwritten with.
export const isRepaymentMethodMismatchedAtom = atom((get) => (
  isRepaymentMethodMismatched(get(paymentMethodAtom), get(loanOfferAtom)?.repayment_method)
));

// Step gate — the Funds step needs a valid draw amount and a selected repayment method.
export const isFundsStepValidAtom = atom(
  (get) => get(isCnuDrawInputValidAtom) && get(isPaymentMethodValidAtom),
);

// Ordered as the fields appear on the page: a rejected Continue sends the customer to the first
// thing that is wrong, not the last.
export const firstFundsErrorAnchorAtom = atom<string | null>((get) => {
  if (!get(isCnuDrawInputValidAtom)) return FIELD_ANCHORS.drawAmount;

  if (!get(isPaymentMethodValidAtom)) return FIELD_ANCHORS.repaymentMethod;

  return null;
});

export const isAuthAgreementValidAtom = atom<boolean>((get) => {
  const reviewed = get(activeReviewedSectionNamesAtom);
  const sections = get(activeContractSectionsAtom);

  if (sections.length === 0) return false;

  return sections.every((section) => reviewed.includes(section.name));
});

// Whether the review-all-documents message should render — only after a rejected Continue.
export const showAuthAgreementErrorAtom = atom<boolean>(
  (get) => get(contractSubmitAttemptedAtom) && !get(isAuthAgreementValidAtom),
);

/** ***********************************
 *************************************
 **** Contract Step Atoms (store) ****
 *************************************
 ************************************ */

export const contractFlowInputsAtom = atom<ContractFlowInputsT | null>((get) => {
  const offer = get(loanOfferAtom);

  if (!offer) return null; // Not "no steps" — "we don't know the flow yet".

  if (!isSupportedContractLoanType(offer.loan_type)) {
    console.warn('Unrecognized loan_type on the loan offer:', offer.loan_type);

    return null; // Unknown flow — better a load state/error than the wrong step order.
  }

  return { loanType: offer.loan_type, stateCode: offer.state };
});

export const contractStepAtom = atom((get) => {
  const inputs = get(contractFlowInputsAtom);

  return inputs ? getContractSteps(inputs) : [];
});

export const saveStepAtom = atom(null, (_get, set, stepKey: ContractStepKeyT) => {
  set(dirtyStepsAtom, (dirty) => ({ ...dirty, [stepKey]: false }));
});

// Gates ContractStepRoute against a direct/deep link to a step the customer hasn't organically
// reached yet (see ContractStep's setFurthestStepIndex, bumped on every accepted Continue).
// Backed by sessionStorage for the same reason as flowIdAtom: a plain atom resets to 0 on a
// mid-flow page refresh, which made ContractStepRoute think a customer legitimately resuming on
// step 2 was trying to skip ahead and bounced them back to step 1. Cleared alongside flowId in
// leaveContractApp so it doesn't leak into a different flow started later in the same tab.
export const furthestStepIndexAtom = atomWithStorage<number>(
  'furthestStepIndex',
  0,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);
